/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { FC, memo, useEffect } from "react";
import { createUseStyles } from "react-jss";
import { motion } from "framer-motion";
import { useDispatch } from "react-redux";

import SingleDestination from "Containers/SingleDestination";
import { areEqualShallow } from "Utils/areEqual";
import { setShowSearchOptions } from "Containers/CampusMap";
import PersonalizationStars from "Assets/images/personalization_stars.svg";
import SearchAIButton from "Assets/images/search_icon.svg";
import styles from "./styles";
import SearchOptionsDestinations from "./SearchOptionsDestinations";

interface Props {
  destinations: Array<Object>;
  setSearchOptionsView: (view: string) => void;
  showDestinations: boolean;
  selectedDestination: string;
  showHideList: (trigger: string) => void;
  currentStop: number;
  showSearchOptions: boolean;
  trailsLength: number;
  googleMap: any;
  showSwitcher: boolean;
  personalizedStops: Array<Object>;
  showSearchAI: boolean;
  setShowSearchPrompt: () => void;
  view: string;
}

const Destinations: FC<any> = memo(
  ({
    destinations,
    setSearchOptionsView,
    showHideList,
    showDestinations,
    selectedDestination,
    currentStop,
    showSearchOptions,
    trailsLength,
    googleMap,
    showSwitcher,
    personalizedStops,
    showSearchAI,
    setShowSearchPrompt,
    view,
  }: Props) => {
    const useStyles = createUseStyles(styles);
    const classes = useStyles();
    const dispatch = useDispatch();

    // Animations //
    // animate open and close //

    const destinationAnimations = {
      open: {
        x: "0%",
        transition: {
          ease: [0.28, 0.57, 0.2, 1],
          duration: 0.5,
        },
      },
      closed: {
        x: "-100%",
        transition: {
          ease: "easeIn",
          duration: 0.25,
          onComplete: () => {
            if (showDestinations) {
              dispatch(setShowSearchOptions(true));
            } else {
              dispatch(setShowSearchOptions(false));
            }
          },
        },
      },
    };

    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        const registerButton = document.getElementById("register-button");
        const lastItem = destinations[destinations.length - 1];
        if (
          event.key === "Tab" &&
          document.activeElement.id === lastItem.title &&
          registerButton
        ) {
          event.preventDefault();
          registerButton.focus();
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, [destinations]);

    return (
      <div
        data-testid="destinations"
        className={`destinations-test-class ${classes.container}`}
      >
        <div
          id="searchOptionsContainer"
          data-testid="searchOptionsContainer"
          className={classes.searchOptionsContainer}
        >
          <SearchOptionsDestinations
            setSearchOptionsView={setSearchOptionsView}
            showDestinations={showDestinations && showSearchOptions}
            showHideList={showHideList}
            trailsCount={trailsLength}
            currentStop={currentStop}
            showSwitcher={showSwitcher}
          />
        </div>
        {view === "destinations" && (
          <motion.div
            initial={{ x: "-100%" }}
            animate={showDestinations ? "open" : "closed"}
            variants={destinationAnimations}
            id="destinationslist"
            data-testid="destinationslist"
            className={classes.destinationsContainer}
          >
            {personalizedStops && (
              <div className={classes.tourLabel} tabIndex={0}>
                <PersonalizationStars
                  className={classes.personalizationStars}
                />
                For You
              </div>
            )}
            {showSearchAI && (
              <button
                className={
                  personalizedStops ? classes.searchWithForYou : classes.search
                }
                data-testid="searchAIButton-destination"
                aria-label="search AI"
                onClick={setShowSearchPrompt}
              >
                <SearchAIButton style={{ cursor: "pointer" }} />
              </button>
            )}
            {showDestinations &&
              destinations.reduce(
                (allDestinations: any, dest: any, index: number) => {
                  if (!dest.isPointOfInterest) {
                    const thumb = dest.photoThumb || "";
                    if (index === personalizedStops?.length) {
                      allDestinations.push(
                        <div
                          className={classes.tourLabel}
                          style={{ height: "40px", paddingLeft: "32px" }}
                          tabIndex={0}
                        >
                          All tour stops
                        </div>
                      );
                    }
                    allDestinations.push(
                      <SingleDestination
                        destination={dest}
                        key={`dest-${dest.id}`}
                        selected={
                          thumb.length > 0 &&
                          selectedDestination?.length > 0 &&
                          selectedDestination.trim() === thumb.trim()
                        }
                        showHideList={showHideList}
                        googleMap={googleMap}
                      />
                    );
                  }
                  return allDestinations;
                },
                []
              )}
          </motion.div>
        )}
      </div>
    );
  },
  areEqualShallow
);

export default Destinations;
try {
    // @ts-ignore
    Destinations.displayName = "Destinations";
    // @ts-ignore
    Destinations.__docgenInfo = { "description": "", "displayName": "Destinations", "props": {} };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/shared/components/Destinations/Destinations.tsx#Destinations"] = { docgenInfo: Destinations.__docgenInfo, name: "Destinations", path: "src/shared/components/Destinations/Destinations.tsx#Destinations" };
}
catch (__react_docgen_typescript_loader_error) { }